import { LivenessFields, lastTelemetryEpoch } from './project-liveness';

/**
 * WHAT "HISTORICAL" MEANS — and why the client no longer bounds it.
 *
 * THE ORIGINAL PROBLEM. Every backfill returned `TRC: []`. The cause was the
 * server's query bounding reads at `DateTime.Now.AddDays(-15)`, so any finished
 * cure older than a fortnight answered empty. Agreed semantic: a historical
 * project's data is its LIFETIME, not "the last N days".
 *
 * THE FIX WAS ENTIRELY SERVER-SIDE, and it landed. The 15-day bound is gone, so
 * `projectName` ALREADY means the project's lifetime — the scoping was never
 * temporal in the first place. Measured on beta-dev, client sending no temporal
 * params at all: project1_2025-03-06_00-12-13 went from 0 rows to 768.
 *
 * SO THE CLIENT SENDS NO TEMPORAL BOUND. This module used to derive one, and
 * shipping it would have caused precisely the silent data loss the whole
 * redesign exists to remove. Both candidate bounds are unsafe, and it is worth
 * recording why, because both look reasonable:
 *
 * A LOWER BOUND FROM THE PROJECT NAME IS VIEWER-DEPENDENT.
 * Projects are named `<label>_YYYY-MM-DD_HH-MM-SS` by the CMS, and that suffix
 * carries no timezone. Reading it as local time means the window depends on who
 * is looking. Measured, same project, same name:
 *
 *     America/Chicago   2025-03-06T06:11:13Z    CLIPS 49 minutes of real data
 *     UTC / London      2025-03-06T00:11:13Z    safe
 *     Asia/Tokyo        2025-03-05T15:11:13Z    safe
 *
 * The first real tick of that project is 2025-03-06T05:23:04Z — BEFORE the
 * name-derived start on the machine that actually runs this. A bounded read
 * would have returned ~720 of 768 rows and opened the chart mid-cure, with
 * nothing on screen saying rows were dropped. An operator in Chicago and one in
 * London would see different data for the same cure.
 *
 * AN UPPER BOUND FROM LAST-TELEMETRY IS ALSO UNSAFE, less obviously. It comes
 * from `lastTelemetryEpoch`, which prefers the derived epoch but falls back to
 * the stored LTR — and the stored value is known to drift in BOTH directions
 * across tenants. A stale LTR that lags the real final tick would clip the END
 * of a cure, which is where the peak temperature lives and the reason anyone
 * opens the chart.
 *
 * Neither bound buys anything today: the server returns exactly this project's
 * rows without them. A bound that can only lose data and cannot save any is not
 * a trade-off, so it is not sent. The transport (fromEpochMs/toEpochMs, built
 * and deployed) stays available for PAGING, where a caller supplies a range it
 * actually knows — see ProjectService.LIFETIME_PARAMS.
 */

/**
 * Start time encoded in a project's name, in epoch milliseconds.
 *
 * DIAGNOSTIC AND DISPLAY ONLY. Do NOT bound a request with this: the suffix
 * carries no timezone, this reads it as the VIEWER's local time, and it has been
 * measured landing 49 minutes after a project's first real tick. See the module
 * comment. Returns null for a name with no such suffix — uploaded and hand-named
 * projects, which are a real case on beta-dev today.
 */
export function projectStartEpoch(projectName: string | null | undefined): number | null {
  if (!projectName) {
    return null;
  }
  const m = /_(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})$/.exec(projectName);
  if (!m) {
    return null;
  }
  const [, y, mo, d, h, mi, sec] = m;
  const t = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(sec)
  ).getTime();
  return Number.isFinite(t) ? t : null;
}

/** An explicit temporal range, supplied by a caller that knows one. */
export interface EpochRange {
  /** EXCLUSIVE lower bound (`timestamp >`), or null for unbounded. */
  fromMs: number | null;
  /** INCLUSIVE upper bound (`timestamp <=`), or null for unbounded. */
  toMs: number | null;
}

/**
 * The temporal range for a normal historical read: UNBOUNDED, both ends.
 *
 * A function rather than a constant so the decision has one place to be revisited
 * and one place to be read, and so a caller reads "the range for this project"
 * rather than a bare null it has to interpret.
 */
export function historicalRangeFor(
  _projectName: string | null | undefined,
  _project: LivenessFields | null | undefined
): EpochRange {
  // Deliberately ignores both arguments. They are the two things a bound could be
  // derived from, and the module comment records the measurement showing each one
  // loses data. Kept in the signature so a future paging caller has the inputs to
  // hand and this stays the place that decides.
  return { fromMs: null, toMs: null };
}

/** True when a range would actually constrain anything. */
export function isBounded(range: EpochRange): boolean {
  return range.fromMs !== null || range.toMs !== null;
}

/** Re-exported so callers reading a lifetime for DISPLAY have one import. */
export function lastTelemetryEpochFor(
  project: LivenessFields | null | undefined
): number | null {
  return lastTelemetryEpoch(project);
}
